import { useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import {
  getAthlete, getAthletes, eastVsWestBase, athletePath, eventPath, formatDate,
  headFor, athleteFor, athleteRecord, athleteRankings, displayCountry, flagFor,
} from '../lib/events';
import { SITE_URL, getStrings, altLinks, type Lang } from '../lib/i18n';
import { BlogHeader } from '../components/BlogHeader';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';
import { CtaBlock } from '../components/CtaBlock';

function Headshot({ name }: { name: string }) {
  const [broken, setBroken] = useState(false);
  const src = headFor(name);
  const initials = name.replace(/"[^"]*"/g, '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <div className="relative shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden bg-elevated border border-line">
      {src && !broken ? (
        <img src={src} alt={name} width={96} height={96} loading="eager" onError={() => setBroken(true)} className="w-full h-full object-cover" />
      ) : (
        <span className="flex items-center justify-center w-full h-full text-xl sm:text-2xl font-bold text-faint">{initials}</span>
      )}
    </div>
  );
}

// Opponent name links to their own athlete page when we have one; otherwise plain text.
function OpponentName({ name, lang }: { name: string; lang: Lang }) {
  const prof = athleteFor(name);
  if (prof) return <Link to={athletePath(lang, prof.slug)} className="hover:text-primary transition">{name}</Link>;
  return <>{name}</>;
}

export default function AthletePage({ lang = 'en' as Lang }: { lang?: Lang }) {
  const { slug } = useParams();
  const t = getStrings(lang).eastVsWest;
  const a = t.athlete;
  const base = eastVsWestBase(lang);
  const ath = slug ? getAthlete(slug) : undefined;
  if (!ath) return <Navigate to={base} replace />;

  const url = `${SITE_URL}${athletePath(lang, ath.slug)}`;
  const country = displayCountry(ath.country);
  const flag = flagFor(ath.country);
  const record = athleteRecord(ath.name);
  const ranks = athleteRankings(ath.name);
  const recTotal = ath.wins != null && ath.losses != null ? `${ath.wins}–${ath.losses}` : null;

  const rows: [string, string | null][] = [
    [a.labels.country, country || null],
    [a.labels.record, recTotal],
    [a.labels.age, ath.age != null ? String(ath.age) : null],
    [a.labels.height, ath.height.cm ? `${ath.height.cm} cm${ath.height.imp ? ` (${ath.height.imp})` : ''}` : null],
    [a.labels.weight, ath.weight.kg ? `${ath.weight.kg} kg${ath.weight.lbs ? ` (${ath.weight.lbs} lb)` : ''}` : null],
    [a.labels.biceps, ath.biceps.cm ? `${ath.biceps.cm} cm${ath.biceps.imp ? ` (${ath.biceps.imp}")` : ''}` : null],
    [a.labels.forearm, ath.forearm.cm ? `${ath.forearm.cm} cm${ath.forearm.imp ? ` (${ath.forearm.imp}")` : ''}` : null],
    [a.labels.wrist, ath.wrist.cm ? `${ath.wrist.cm} cm${ath.wrist.imp ? ` (${ath.wrist.imp}")` : ''}` : null],
  ];

  const title = `${ath.name}${ath.nickname ? ` "${ath.nickname}"` : ''} — ${a.titleSuffix} | ArmProgress`;
  const description = `${ath.name}${country ? `, ${country}` : ''}${recTotal ? ` · ${recTotal}` : ''}${ath.height.cm ? ` · ${ath.height.cm} cm` : ''}${ath.weight.kg ? `, ${ath.weight.kg} kg` : ''}. ${a.recordHeading} + ${a.statsHeading.toLowerCase()}.`;

  const personLd = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: ath.name,
    alternateName: ath.nickname || undefined,
    nationality: country || undefined,
    height: ath.height.cm ? `${ath.height.cm} cm` : undefined,
    weight: ath.weight.kg ? `${ath.weight.kg} kg` : undefined,
    url,
  };

  return (
    <div className="min-h-screen bg-dark">
      <Seo
        title={title}
        description={description}
        canonical={url}
        type="article"
        lang={lang}
        jsonLd={personLd}
        alternates={altLinks(`/east-vs-west/athlete/${ath.slug}`)}
      />
      <BlogHeader lang={lang} />

      <article className="container mx-auto px-6 py-12 max-w-3xl">
        <a href={base} className="text-faint hover:text-ink text-sm">{a.back}</a>

        {/* Header */}
        <div className="mt-4 mb-8 flex items-center gap-4 sm:gap-5">
          <Headshot name={ath.name} />
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-ink leading-tight break-words">{ath.name}</h1>
            {ath.nickname && <p className="text-primary font-bold mt-1">“{ath.nickname}”</p>}
            <p className="text-muted mt-1 text-sm sm:text-base">{flag && <span className="mr-1.5">{flag}</span>}{country}{recTotal ? <span className="text-faint"> · {recTotal}</span> : null}</p>
          </div>
        </div>

        {/* Physical stats — omitted entirely for record-only athletes (no goldsarm data) */}
        {rows.some(([, v]) => v) && (
          <>
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{a.statsHeading}</h2>
            <div className="rounded-2xl border border-line bg-surface/60 divide-y divide-line overflow-hidden mb-10">
              {rows.filter(([, v]) => v).map(([label, v]) => (
                <div key={label} className="flex items-center justify-between gap-4 px-4 py-3">
                  <span className="text-faint text-sm">{label}</span>
                  <span className="text-ink font-semibold text-right">{v}</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Official rankings */}
        {ranks.length > 0 && (
          <>
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{a.rankHeading}</h2>
            <div className="flex flex-wrap gap-2 mb-10">
              {ranks.map((r, i) => (
                <span key={i} className={`text-sm font-semibold px-3 py-1.5 rounded-full border ${r.champion ? 'bg-primary/10 border-primary/40 text-primary' : 'bg-surface border-line text-muted'}`}>
                  {r.champion ? '🏆 ' : `#${r.rank} `}{r.className} · {t.arm[r.arm]}
                </span>
              ))}
            </div>
          </>
        )}

        {/* EVW fight record */}
        <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{a.recordHeading}</h2>
        {record.length === 0 ? (
          <p className="text-muted mb-10">{a.noRecord}</p>
        ) : (
          <div className="rounded-2xl border border-line bg-surface/60 divide-y divide-line overflow-hidden mb-10">
            {record.map((b, i) => {
              // EVENT_ARCHIVE stores the score winner-first; re-orient it to THIS
              // athlete's perspective (their tally first) so the row is unambiguous.
              const [x, y] = b.score.split('-');
              const score = (b.won ? `${x}–${y}` : `${y}–${x}`);
              return (
                <div key={i} className="flex items-center gap-3 p-4">
                  <span className={`shrink-0 min-w-[3.25rem] text-center whitespace-nowrap text-[11px] font-black uppercase tracking-wide px-2.5 py-1 rounded-full border ${b.won ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' : 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30'}`}>
                    {b.won ? a.win : a.loss}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-ink font-semibold leading-tight truncate">
                      <OpponentName name={b.opponent} lang={lang} />
                    </div>
                    <div className="text-faint text-xs truncate">
                      <Link to={eventPath(lang, b.eventSlug)} className="hover:text-primary transition">{b.eventName}</Link>
                      {' · '}{formatDate(b.isoDate)} · {t.arm[b.arm as 'right' | 'left']}{b.weightClass ? ` · ${b.weightClass}` : ''}
                    </div>
                  </div>
                  <span className={`shrink-0 font-extrabold tabular-nums ${b.won ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} aria-label={`${b.won ? a.win : a.loss} ${score}`}>
                    {score}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Biography */}
        {ath.bio.length > 0 && (
          <>
            <h2 className="text-ink font-bold text-sm uppercase tracking-wide mb-3">{a.bioHeading}</h2>
            <div className="space-y-3 text-muted leading-relaxed mb-10">
              {ath.bio.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </>
        )}

        <CtaBlock lang={lang} />
      </article>

      <Footer lang={lang} />
    </div>
  );
}

export { getAthletes };
